import { motion } from "framer-motion";
import { Lock, Phone } from "lucide-react";
import { Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isValidBdPhone, login } from "../../../services/auth.api";

/**
 * The API authenticates on a Bangladeshi phone number and password — there is
 * no e-mail login, so this form no longer branches on the visitor's region.
 */
const LoginForm = ({ onRequireVerification }) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm({
    mode: "onTouched",
    defaultValues: { phone: "", userPassword: "" },
  });

  const primaryColor = "#1B6DC1";

  const onSubmit = async (data) => {
    try {
      await login({ phone: data.phone, password: data.userPassword });
      navigate("/");
    } catch (err) {
      // An account that never finished OTP verification can still log in
      // nowhere — send it back to the code screen instead of a dead end.
      if (/verif/i.test(err?.message || "")) {
        onRequireVerification?.(data.phone);
        navigate("/authentication/verify");
        return;
      }
      setError("root", { message: err?.message || "Login failed. Please try again." });
      toast.error(err?.message || "Login failed. Please try again.");
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, x: -50, transition: { duration: 0.4 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.3 } }),
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="d-flex flex-column p-1 p-lg-4 gap-2"
    >
      <motion.div custom={0} variants={itemVariants}>
        <h2 className="text-center fw-bold text-dark" style={{ fontSize: "1.875rem" }}>
          SIGN IN
        </h2>
      </motion.div>

      <motion.div custom={1} variants={itemVariants} className="d-flex flex-column gap-3">
        {errors.root && (
          <div className="alert alert-danger py-2 mb-0" role="alert">
            {errors.root.message}
          </div>
        )}

        <div>
          <InputGroup>
            <InputGroup.Text
              className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.phone ? "border-danger" : ""}`}
            >
              <Phone size={20} className="text-secondary" />
            </InputGroup.Text>
            <Form.Control
              type="tel"
              placeholder="01712345678"
              {...register("phone", {
                required: "Phone number is required",
                validate: (v) =>
                  isValidBdPhone(v) || "Enter a valid Bangladeshi phone number (e.g. 01712345678)",
              })}
              className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.phone ? "border-danger" : ""}`}
            />
          </InputGroup>
          {errors.phone && (
            <small className="text-danger d-block mt-1 ms-1">{errors.phone.message}</small>
          )}
        </div>

        <div>
          <InputGroup>
            <InputGroup.Text
              className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.userPassword ? "border-danger" : ""}`}
            >
              <Lock size={20} className="text-secondary" />
            </InputGroup.Text>
            <Form.Control
              type="password"
              placeholder="Password"
              {...register("userPassword", {
                required: "Password is required",
                minLength: { value: 8, message: "Password must be at least 8 characters" },
              })}
              className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.userPassword ? "border-danger" : ""}`}
            />
          </InputGroup>
          {errors.userPassword && (
            <small className="text-danger d-block mt-1">{errors.userPassword.message}</small>
          )}
        </div>
      </motion.div>

      <motion.div
        custom={2}
        variants={itemVariants}
        className="d-flex align-items-center justify-content-between"
      >
        <Form.Check
          type="checkbox"
          id="remember-me"
          label="Remember me"
          className="text-secondary d-flex align-items-center gap-1 cursor-pointer"
        />
        <Button
          variant="link"
          className="p-0 fw-medium text-decoration-none"
          style={{ color: primaryColor }}
          onClick={() => navigate("/authentication/forget-password")}
        >
          Forgot password?
        </Button>
      </motion.div>

      <motion.div custom={3} variants={itemVariants}>
        <Button
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          variant="primary"
          className="w-100 rounded-start-pill py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
          style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
        >
          Sign In
          {isSubmitting && <Spinner size="sm" animation="border" />}
        </Button>
      </motion.div>

      <motion.p custom={4} variants={itemVariants} className="text-center text-secondary mb-0">
        Don't have an account?{" "}
        <Link
          to="/authentication/register"
          className="p-0 fw-semibold text-decoration-none align-baseline"
          style={{ color: primaryColor, fontSize: "1rem" }}
        >
          Sign Up
        </Link>
      </motion.p>
    </motion.div>
  );
};

export default LoginForm;
